import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, Search, MapPin, Navigation, Check, Loader2, AlertCircle
} from 'lucide-react';
import './LocationSheet.css';

/**
 * LocationSheet - Modern Bottom Sheet for Location Selection
 * With GPS support and search filtering
 */
function LocationSheet({ 
  open, 
  onClose, 
  onSelect, 
  selectedState, 
  states = [],
  isArabic = false 
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const searchInputRef = useRef(null);
  const sheetRef = useRef(null);
  
  // Filter states based on search
  const filteredStates = useMemo(() => {
    if (!searchQuery.trim()) {
      return states;
    }
    const query = searchQuery.toLowerCase();
    return states.filter(state => {
      const nameEn = state.name_en?.toLowerCase() || '';
      const nameAr = state.name_ar || '';
      return nameEn.includes(query) || nameAr.includes(query);
    });
  }, [states, searchQuery]);
  
  // Reset search when sheet closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setIsClosing(false);
      setGpsError(null);
    }
  }, [open]);
  
  // Lock body scroll when sheet is open
  useEffect(() => {
    if (!open) return;
    
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [open]);
  
  // Focus search input when sheet opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);
  
  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };
  
  // Handle state selection
  const handleSelect = (stateKey) => {
    onSelect(stateKey);
    handleClose();
  };
  
  // GPS: Map coordinates to nearest state
  const mapCoordsToState = (lat, lng) => {
    // Simplified mapping based on Sudan geography
    // Khartoum region
    if (lat >= 15.3 && lat <= 16.0 && lng >= 32.0 && lng <= 33.5) {
      return 'khartoum';
    }
    // Gezira region
    if (lat >= 13.5 && lat <= 15.3 && lng >= 32.5 && lng <= 34.0) {
      return 'gezira';
    }
    // Gedaref region
    if (lat >= 13.0 && lat <= 15.0 && lng >= 34.0 && lng <= 36.5) {
      return 'gedaref';
    }
    // Kassala region
    if (lat >= 14.5 && lat <= 17.0 && lng >= 35.5 && lng <= 37.0) {
      return 'kassala';
    }
    // North Kordofan
    if (lat >= 11.0 && lat <= 14.0 && lng >= 27.0 && lng <= 31.0) {
      return 'north_kordofan';
    }
    // South Darfur
    if (lat >= 9.0 && lat <= 12.5 && lng >= 23.0 && lng <= 27.5) {
      return 'south_darfur';
    }
    // Red Sea
    if (lat >= 18.0 && lat <= 22.0 && lng >= 35.0 && lng <= 38.0) {
      return 'red_sea';
    }
    // Northern
    if (lat >= 18.0 && lat <= 22.0 && lng >= 29.0 && lng <= 35.0) {
      return 'northern';
    }
    // River Nile
    if (lat >= 16.0 && lat <= 20.0 && lng >= 32.0 && lng <= 35.0) {
      return 'river_nile';
    }
    // Default to Khartoum if can't determine
    return 'khartoum';
  };
  
  // Handle GPS location request
  const handleGpsClick = async () => {
    if (!navigator.geolocation) {
      setGpsError(isArabic 
        ? 'المتصفح لا يدعم تحديد الموقع'
        : 'Browser does not support geolocation');
      return;
    }
    
    setGpsLoading(true);
    setGpsError(null);
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes cache
        });
      });
      
      const { latitude, longitude } = position.coords;
      const detectedState = mapCoordsToState(latitude, longitude);
      
      // Select the detected state
      handleSelect(detectedState);
      
    } catch (error) {
      console.error('GPS Error:', error);
      let errorMessage;
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = isArabic 
            ? 'تم رفض الإذن للوصول للموقع'
            : 'Location permission denied';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = isArabic 
            ? 'لم نتمكن من تحديد موقعك'
            : 'Unable to determine location';
          break;
        case error.TIMEOUT:
          errorMessage = isArabic 
            ? 'انتهى الوقت المحدد لتحديد الموقع'
            : 'Location request timed out';
          break;
        default:
          errorMessage = isArabic 
            ? 'لم نتمكن من تحديد موقعك. الرجاء اختيار الولاية يدويًا.'
            : 'Unable to detect location. Please select manually.';
      }
      
      setGpsError(errorMessage);
    } finally {
      setGpsLoading(false);
    }
  };
  
  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);
  
  if (!open) return null;
  
  return (
    <div 
      className={`location-sheet-overlay ${isClosing ? 'is-closing' : ''}`}
      onClick={handleBackdropClick}
      data-testid="location-sheet-overlay"
    >
      <div 
        ref={sheetRef}
        className={`location-sheet ${isClosing ? 'is-closing' : ''}`}
        dir={isArabic ? 'rtl' : 'ltr'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-sheet-title"
        data-testid="location-sheet"
      >
        {/* Handle */}
        <div className="sheet-handle-area">
          <div className="sheet-handle" />
        </div>
        
        {/* Header */}
        <div className="sheet-header">
          <h2 id="location-sheet-title" className="sheet-title">
            {isArabic ? 'اختر الموقع' : 'Select Location'}
          </h2>
          <button
            type="button"
            className="sheet-close"
            onClick={handleClose}
            aria-label="Close"
            data-testid="location-sheet-close"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* GPS Button */}
        <div className="gps-section">
          <button
            type="button"
            className="gps-button"
            onClick={handleGpsClick}
            disabled={gpsLoading}
            data-testid="gps-button"
          >
            {gpsLoading ? (
              <Loader2 size={20} className="spinning" />
            ) : (
              <Navigation size={20} />
            )}
            <span>
              {isArabic ? 'استخدم موقعي الحالي' : 'Use My Current Location'}
            </span>
          </button>
          
          {gpsError && (
            <div className="gps-error">
              <AlertCircle size={14} />
              <span>{gpsError}</span>
            </div>
          )}
        </div>
        
        {/* Divider */}
        <div className="sheet-divider">
          <span>{isArabic ? 'أو اختر يدويًا' : 'or select manually'}</span>
        </div>
        
        {/* Search */}
        <div className="sheet-search">
          <Search size={18} className="search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={isArabic ? 'ابحث عن الولاية...' : 'Search state...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            dir={isArabic ? 'rtl' : 'ltr'}
            inputMode="search"
            autoComplete="off"
            data-testid="location-search"
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
              type="button"
            >
              <X size={16} />
            </button>
          )}
        </div>
        
        {/* States List */}
        <div className="states-list" role="listbox">
          {filteredStates.length === 0 ? (
            <div className="no-results">
              {isArabic ? 'لا توجد نتائج' : 'No results'}
            </div>
          ) : (
            filteredStates.map((state) => {
              const isSelected = selectedState === state.key;
              
              return (
                <button
                  key={state.key}
                  type="button"
                  className={`state-option ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(state.key)}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`state-option-${state.key}`}
                >
                  <MapPin size={18} className="state-icon" />
                  <span className="state-name">
                    {isArabic ? state.name_ar : state.name_en}
                  </span>
                  {isSelected && (
                    <Check size={18} className="check-icon" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationSheet;
